import { cn } from '@/lib/cn.ts';
import { type VariantProps, cva } from 'class-variance-authority';
import { forwardRef } from 'react';
import { Spinner } from './Spinner.tsx';

const button = cva(
  [
    'relative inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium',
    'transition-[transform,background-color,border-color,box-shadow,opacity] duration-150',
    'ease-[cubic-bezier(0.16,1,0.3,1)] active:scale-[0.98]',
    'disabled:pointer-events-none disabled:opacity-45',
  ],
  {
    variants: {
      variant: {
        // Crimson — the single dominant call to action.
        primary:
          'bg-accent text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_10px_30px_-14px_var(--accent)] hover:bg-accent-hover',
        // Gold — positive confirmation (payment taken, checked in).
        gold: 'bg-gold text-[#1c160e] shadow-[inset_0_1px_0_rgba(255,255,255,0.25)] hover:brightness-[1.06]',
        secondary:
          'border border-border bg-bg-elevated text-text hover:border-border-strong hover:bg-surface',
        ghost: 'text-text-muted hover:bg-surface hover:text-text',
        danger: 'border border-danger/35 text-danger hover:border-danger/60 hover:bg-accent-faint',
      },
      size: {
        sm: 'h-8 px-3 text-xs',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-5 text-[15px]',
        xl: 'h-16 px-7 text-base tracking-tight',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

type Props = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof button> & {
    loading?: boolean;
    icon?: React.ReactNode;
  };

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { className, variant, size, loading, icon, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={cn(button({ variant, size }), className)}
      {...rest}
    >
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner />
        </span>
      )}
      <span className={cn('inline-flex items-center gap-2', loading && 'opacity-0')}>
        {icon}
        {children}
      </span>
    </button>
  );
});
